import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { centralApi } from '@marque/api/central'
import type { ModuleLifecycleResult } from '@marque/api/central'

// AB#2149 — lifecycle actions for one installed module (AB#2081 back-end): the advisory deprecate
// toggle, the gated hard uninstall, and the destructive revoke-all cascade behind a typed-module-id
// confirmation. Lives on the Modules page next to the change feed.

export function ModuleLifecyclePanel({ moduleId, packageId, deprecated, onUninstalled }: {
    moduleId: string
    /** Latest package id — the id every /api/modules/{id} lifecycle verb takes. */
    packageId: string
    deprecated: boolean
    onUninstalled: () => void
}) {
    const queryClient = useQueryClient()
    const [showUninstall, setShowUninstall] = useState(false)

    const deprecateMutation = useMutation({
        mutationFn: () => deprecated
            ? centralApi.undeprecateModulePackage(packageId)
            : centralApi.deprecateModulePackage(packageId),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['module-packages'] }),
    })

    return (
        <div className="flex items-center gap-2 mb-4">
            <span className="text-sm font-medium text-neutral-700 flex-1 min-w-0 font-mono">{moduleId}</span>
            <button
                type="button"
                data-testid="toggle-deprecate"
                onClick={() => deprecateMutation.mutate()}
                disabled={deprecateMutation.isPending}
                className="text-xs px-2 py-1 border border-neutral-200 rounded text-neutral-600 hover:bg-neutral-50 disabled:opacity-40"
            >
                {deprecated ? 'Undeprecate' : 'Deprecate'}
            </button>
            <button
                type="button"
                data-testid="open-uninstall"
                onClick={() => setShowUninstall(true)}
                className="text-xs px-2 py-1 border border-red-200 rounded text-red-700 hover:bg-red-50"
            >
                Uninstall…
            </button>

            {showUninstall && (
                <UninstallDialog
                    moduleId={moduleId}
                    packageId={packageId}
                    onClose={() => setShowUninstall(false)}
                    onUninstalled={() => {
                        setShowUninstall(false)
                        void queryClient.invalidateQueries({ queryKey: ['module-packages'] })
                        // The grant picker (TenantModules) caches the catalog for 5 min — the
                        // uninstalled module must vanish from it immediately.
                        void queryClient.invalidateQueries({ queryKey: ['module-catalog'] })
                        onUninstalled()
                    }}
                />
            )}
        </div>
    )
}

function removedSummary(removed: Record<string, number | boolean>): string {
    const counts = Object.entries(removed)
        .filter((e): e is [string, number] => typeof e[1] === 'number' && e[1] > 0)
        .map(([k, v]) => `${v} ${k}`)
    return counts.length > 0 ? counts.join(', ') : 'nothing left to remove'
}

// The one dialog handles the whole ladder: plain uninstall → blocked-with-reasons → the scary
// revoke-all path, armed only by typing the exact module id.
function UninstallDialog({ moduleId, packageId, onClose, onUninstalled }: {
    moduleId: string
    packageId: string
    onClose: () => void
    onUninstalled: () => void
}) {
    const [result, setResult] = useState<ModuleLifecycleResult | null>(null)
    const [typedId, setTypedId] = useState('')
    const [busy, setBusy] = useState(false)

    const run = async (fn: () => Promise<ModuleLifecycleResult>) => {
        setBusy(true)
        try { setResult(await fn()) } finally { setBusy(false) }
    }

    const done = result?.ok === true
    const blocked = result != null && !result.ok ? result : null
    const entitlementOnlyBlock = blocked?.blockers != null
        && blocked.blockers.activeEntitledCompanies > 0
        && blocked.blockers.dependentModuleIds.length === 0

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                {!done && (
                    <>
                        <h2 className="text-base font-semibold text-neutral-900 mb-1">{`Uninstall ${moduleId}?`}</h2>
                        <p className="text-sm text-neutral-500 mb-4">
                            Removes the module&apos;s entire catalogue footprint (all versions) from this environment.
                            Tenant record data is left intact and becomes resolvable again on re-install.
                        </p>

                        {blocked && !entitlementOnlyBlock && (
                            <div data-testid="uninstall-blocked" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4 space-y-1">
                                <p>{blocked.message}</p>
                                {blocked.blockers != null && blocked.blockers.dependentModuleIds.length > 0 && (
                                    <p>{`Dependent modules: ${blocked.blockers.dependentModuleIds.join(', ')} — uninstall these first.`}</p>
                                )}
                            </div>
                        )}

                        {entitlementOnlyBlock && blocked?.blockers != null && (
                            <div data-testid="revoke-all-section" className="border border-red-200 bg-red-50 rounded-lg px-3 py-3 mb-4">
                                <p className="text-xs font-semibold text-red-800 mb-1">
                                    {`${blocked.blockers.activeEntitledCompanies} company(ies) are still entitled to this module.`}
                                </p>
                                <p className="text-xs text-red-700 mb-2">
                                    You can revoke the module from <b>all tenants</b> and uninstall it in one step.
                                    Every revocation is audited and takes effect immediately. This cannot be undone.
                                    Type the full module id to arm this action.
                                </p>
                                <input
                                    data-testid="confirm-module-id"
                                    value={typedId}
                                    onChange={e => setTypedId(e.target.value)}
                                    placeholder={moduleId}
                                    className="w-full border border-red-300 rounded px-2 py-1 text-sm font-mono mb-2 bg-white"
                                />
                                <button
                                    type="button"
                                    data-testid="confirm-revoke-all"
                                    disabled={typedId !== moduleId || busy}
                                    onClick={() => void run(() => centralApi.revokeAllAndUninstallModule(packageId, typedId))}
                                    className="w-full px-3 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
                                >
                                    {busy ? 'Working…' : `Revoke from all tenants & uninstall ${moduleId}`}
                                </button>
                            </div>
                        )}

                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 px-3 py-2 text-sm rounded-md border border-neutral-200 text-neutral-600 hover:bg-neutral-50"
                            >
                                Cancel
                            </button>
                            {!entitlementOnlyBlock && (
                                <button
                                    type="button"
                                    data-testid="confirm-uninstall"
                                    disabled={busy}
                                    onClick={() => void run(() => centralApi.uninstallModulePackage(packageId))}
                                    className="flex-1 px-3 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
                                >
                                    {busy ? 'Working…' : 'Uninstall'}
                                </button>
                            )}
                        </div>
                    </>
                )}

                {done && result?.ok === true && (
                    <>
                        <h2 className="text-base font-semibold text-neutral-900 mb-1">{`Uninstalled ${moduleId}`}</h2>
                        {typeof result.revokedEntitlements === 'number' && result.revokedEntitlements > 0 && (
                            <p className="text-sm text-neutral-600 mb-1">
                                {`Revoked ${result.revokedEntitlements} entitlement(s).`}
                            </p>
                        )}
                        <p data-testid="removed-summary" className="text-xs text-neutral-500 mb-4">{`Removed: ${removedSummary(result.removed)}.`}</p>
                        <button
                            type="button"
                            data-testid="uninstall-done-close"
                            onClick={onUninstalled}
                            className="w-full px-3 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
                        >
                            Close
                        </button>
                    </>
                )}
            </div>
        </div>
    )
}
